import dayjs, { type Dayjs } from '@/config/dayjs';
import { DataRangeFilterEnum, getDateRangeFromFilter } from '@/constants/dates';
import type { useLocalization } from '@/hooks/useLocalization';
import { parseAppDate } from '@/utils/date';

/** A single-day preset for a date picker. */
export interface SingleDateShortcut {
  label: string;
  getValue: () => Dayjs;
}

/** The last day of a closed period preset -- Last Quarter or Last Year. */
function endOfClosedPeriod(
  filter: DataRangeFilterEnum.ClosedQuarter | DataRangeFilterEnum.ClosedYear,
): Dayjs {
  return parseAppDate(getDateRangeFromFilter(filter).endDate) ?? dayjs();
}

/**
 * Legacy's presets for an as-of date (`Shared/Components/DatePicker.jsx`),
 * labelled in the advisor's language. An as-of date is one day, so each preset
 * is a single day -- the end of the period it names, taken from the app's
 * closed quarter and year ranges -- rather than a range.
 */
export function asOfDateShortcuts(
  t: ReturnType<typeof useLocalization>['t'],
): SingleDateShortcut[] {
  return [
    { label: t('asOfDate.today', 'Today'), getValue: () => dayjs() },
    {
      label: t('asOfDate.lastQuarter', 'Last Quarter'),
      getValue: () => endOfClosedPeriod(DataRangeFilterEnum.ClosedQuarter),
    },
    {
      label: t('asOfDate.lastYear', 'Last Year'),
      getValue: () => endOfClosedPeriod(DataRangeFilterEnum.ClosedYear),
    },
  ];
}

/**
 * The earliest as-of date offered, as legacy's historical picker allowed
 * (`Shared/Components/Historical/DatePicker.jsx`): the start of the month three
 * months before yesterday. Balances as of a date come from the stored history,
 * so an earlier day shows no accounts at all.
 */
export function earliestAsOfDate(today: Dayjs = dayjs()): Dayjs {
  return today.subtract(1, 'day').subtract(3, 'month').startOf('month');
}
